// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';
import { getAvatarColor, getAvatarTextColor } from '@/lib/avatar-color';

/**
 * Generate initials from a full name string.
 * "Иванов Артем" → "ИА"
 * "Artem" → "Ar"
 */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (parts.length >= 2) {
    // Has space: take first letter of first two words
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }

  // Single word: take first 2 characters
  return name.slice(0, 2).toUpperCase();
}

interface UserAvatarProps {
  src?: string;
  alt?: string;
  seed?: string;
  name?: string;
  status?: string;
  fontSize?: number;
  size?: number;
  className?: string;
}

// TODO: pass everywhere size and fontSize
export const UserAvatar = ({
  src,
  alt,
  seed,
  className,
  fontSize = 12,
  size = 7,
  name = 'User',
}: UserAvatarProps) => {
  const [imageError, setImageError] = useState(false);

  const displayName = name?.trim() || 'User';
  const hasImage = Boolean(src?.trim()) && !imageError;

  let colorKey = displayName;
  if (seed)
    colorKey = seed;

  const bgColor = getAvatarColor(colorKey, 'dark');
  const textColor = getAvatarTextColor(bgColor, 'dark');

  const avatarSize = `${size * 4}px`;

  useEffect(() => {
    setImageError(false);
  }, [src]);

  if (!hasImage) {
    return (
      <div
        style={{
          width: avatarSize,
          height: avatarSize,
          minWidth: avatarSize,
          minHeight: avatarSize,
          fontSize,
          backgroundColor: bgColor,
          color: textColor,
        }}
        className={cn(
          'flex shrink-0 items-center justify-center rounded-full font-bold',
          className
        )}
      >
        {getInitials(displayName)}
      </div>
    );
  }

  return (
    <Avatar
      className={cn('shrink-0 rounded-full', className)}
      style={{
        width: avatarSize,
        height: avatarSize,
      }}
    >
      <AvatarImage src={src} alt={alt || displayName} onError={() => setImageError(true)} />
    </Avatar>
  );
};

export const UserAvatarDeprecated = ({
  src,
  alt,
  className,
  fontSize = 12,
  size = 7,
  name = 'User',
}: UserAvatarProps) => {
  if (!src) {
    const bgColor = getAvatarColor(name, 'dark');
    const textColor = getAvatarTextColor(bgColor, 'dark');
    return (
      <div
        style={{
          fontSize: fontSize,
          background: bgColor,
          color: textColor,
        }}
        className={cn(
          `flex text-center items-center justify-center h-${size} w-${size} md:h-10 md:w-10 font-bold rounded-full`,
          className
        )}
      >
        {getInitials(name)}
      </div>
    );
  }

  return (
    // rounded-lg overflow-hidden
    <Avatar
      className={cn(`h-${size} w-${size} md:h-10 md:w-10`, className)}
    >
      <AvatarImage src={src} alt={alt} />
    </Avatar>
  );
};

export const UserAvatarActivityCard = ({
                             src,
                             alt,
                             className,
                             fontSize = 6,
                             size = 4,
                             name = 'User',
                           }: UserAvatarProps) => {
  if (!src) {
    const bgColor = getAvatarColor(name, 'dark');
    const textColor = getAvatarTextColor(bgColor, 'dark');
    return (
      <div
        style={{
          fontSize: fontSize,
          background: bgColor,
          color: textColor,
        }}
        className={cn(
          `flex text-center items-center justify-center h-${size} w-${size} font-bold rounded-full`,
          className
        )}
      >
        {getInitials(name)}
      </div>
    );
  }

  return (
    <Avatar className={cn(`h-${size} w-${size}`, className)}>
      <AvatarImage src={src} alt={alt} />
    </Avatar>
  );
};
